import React, { useEffect } from "react"
import { StyleSheet, Text, View } from "react-native"
import { useNavigation } from "@react-navigation/native"
import { connect } from "react-redux"
import * as amplitude from "@amplitude/analytics-react-native"
import FilledButtonM from "@common/components/button/FilledButtonM"
import FunnelStepHeader from "@common/components/funnel/FunnelStepHeader"
import FunnelStepLayout from "@common/components/funnel/FunnelStepLayout"
import { FUNNEL_SCENES } from "@common/components/funnel/funnelScenes"
import { theme } from "@common/theme/theme"
import { elide } from "@common/utils/activationSuiteDisplay"
import {
    gridDisplayOffsetHours,
    resolveFunnelUserTimezone,
} from "@common/student-diagnostic-stepper/constraintsGrid"
import { formatEuros } from "../brevet/utils/formatEuros"
import { useInscriptionFunnel } from "./context/InscriptionFunnelContext"
import { useInscriptionFunnelGuard } from "./hooks/useInscriptionFunnelGuard"
import {
    INSCRIPTION_NEXT_STEPS,
    INSCRIPTION_STEPS,
    firstChargeWhen,
    formatSlotsLabel,
} from "./constants/inscriptionContent"

/**
 * Retour de paiement — « c'est réservé, voici ce qui se passe maintenant ».
 *
 * Stripe ramène le parent ici, éventuellement dans un onglet FROID (3-D Secure
 * sur mobile) : l'URL de retour porte donc `?user=`, et la garde du funnel
 * réhydrate le parcours avant d'afficher l'écran.
 *
 * Il n'est pas terminal : le CTA enchaîne sur le téléchargement de l'app, par
 * laquelle l'enfant suivra les séances.
 */
const InscriptionConfirmationScreen = ({ userTimezone, serverTimezone }) => {
    const navigation = useNavigation()
    const { state } = useInscriptionFunnel()
    // `nextParams` propage le `?user=` : l'écran suivant doit pouvoir se
    // réhydrater de la même façon.
    const { nextParams } = useInscriptionFunnelGuard()

    const quote = state.quote
    const firstname = state.studentFirstname || "votre enfant"

    useEffect(() => {
        amplitude.track("inscription_funnel_confirmation_viewed", {
            funnel_state_id: state.funnelStateId,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <FunnelStepLayout
            scene={FUNNEL_SCENES.confirmation}
            contentContainerStyle={styles.content}
            header={
                <FunnelStepHeader
                    step={INSCRIPTION_STEPS.checkout}
                    total={INSCRIPTION_STEPS.total}
                    hideBack
                />
            }
            footer={
                <View style={styles.footer}>
                    <FilledButtonM
                        label="Continuer"
                        onPress={() =>
                            navigation.navigate(
                                "inscription/app-download",
                                nextParams
                            )
                        }
                        style={{ width: "100%", marginBottom: 0 }}
                    />
                </View>
            }
        >
            <Text style={styles.title}>
                La place {elide(firstname)} est réservée
            </Text>
            {quote?.first_session_label ? (
                <Text style={styles.subtitle}>
                    Premier cours : {quote.first_session_label}
                </Text>
            ) : null}

            <View style={styles.card}>
                <Text style={styles.cardTitle}>Ce qui vous attend</Text>
                {INSCRIPTION_NEXT_STEPS.map((step, index) => (
                    <View key={step} style={styles.stepRow}>
                        <View style={styles.stepIndex}>
                            <Text style={styles.stepIndexText}>
                                {index + 1}
                            </Text>
                        </View>
                        <Text style={styles.stepText}>{step}</Text>
                    </View>
                ))}
            </View>

            {state.slots?.length ? (
                <Text style={styles.slots}>
                    Créneaux retenus :{" "}
                    {formatSlotsLabel(
                        state.slots,
                        gridDisplayOffsetHours(userTimezone, serverTimezone)
                    )}
                </Text>
            ) : null}

            {/* Montant lu sur le devis SERVEUR, jamais écrit en dur : c'est le
                devis gravé au paiement. Sans devis (reprise dégradée), on
                retire la phrase plutôt que d'affirmer un montant supposé. */}
            <Text style={styles.footnote}>
                {quote?.deposit_cents
                    ? `Vous avez payé ${formatEuros(quote.deposit_cents / 100)} aujourd'hui. Le solde`
                    : "Le solde"}{" "}
                de votre premier mois sera prélevé{" "}
                {firstChargeWhen(quote, "le jour du premier cours")}.
            </Text>
        </FunnelStepLayout>
    )
}

const styles = StyleSheet.create({
    content: { paddingBottom: 32 },
    title: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 22,
        lineHeight: 29,
        color: theme.colors.primaryDark || theme.colors.primary,
        marginBottom: 6,
    },
    subtitle: {
        fontFamily: "Montserrat-Bold",
        fontSize: 15,
        color: theme.colors.primary,
    },
    card: {
        marginTop: 22,
        padding: 16,
        borderRadius: 12,
        backgroundColor: theme.colors.whiteAlmost,
        gap: 14,
    },
    cardTitle: {
        fontFamily: "Montserrat-Bold",
        fontSize: 14,
        color: theme.colors.black,
    },
    stepRow: { flexDirection: "row", alignItems: "flex-start", gap: 10 },
    stepIndex: {
        width: 22,
        height: 22,
        borderRadius: 11,
        backgroundColor: theme.colors.material.primary["50"],
        alignItems: "center",
        justifyContent: "center",
    },
    stepIndexText: {
        fontFamily: "Montserrat-Bold",
        fontSize: 11,
        color: theme.colors.primary,
    },
    stepText: {
        flex: 1,
        fontFamily: "Montserrat-Regular",
        fontSize: 13,
        lineHeight: 19,
        color: theme.colors.grey,
    },
    slots: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.grey,
        marginTop: 18,
    },
    footnote: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        lineHeight: 18,
        color: theme.colors.grey,
        marginTop: 18,
    },
    footer: {
        paddingVertical: 14,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
})

const mapStateToProps = (state) => ({
    // Cf. l'écran de paiement : les créneaux se disent à l'heure où le parent
    // les a cochés, pas à celle de Paris.
    //
    // Cf. resolveFunnelUserTimezone (repli sur l'appareil à la lecture).
    userTimezone: resolveFunnelUserTimezone(
        state.data?.inscriptionFunnel?.userTimezone
    ),
    serverTimezone: state.data?.inscriptionFunnel?.serverTimezone,
})

export default connect(mapStateToProps)(InscriptionConfirmationScreen)
